import { AnyAction, createAsyncThunk, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';

import { isTokenValid } from '../../helpers/jwt';
import { RootState } from '../store';
import {
    getAccessTokenFromCodeService,
    getAuthorizationUrlService,
    redirectToAuthorizationUrl,
    refreshAccessTokenService,
    revokeRefreshTokenService,
} from './authenticationApi';
import { AuthenticationData, AuthenticationState } from './authenticationTypes';

export const initialAuthenticationState: AuthenticationState = {
    authenticationState: undefined,
    authenticationData: undefined,
};

// Async thunks

/**
 * Given an authentication code and state, attempt to get a new
 * access token from the authentication service
 */
type AccessTokenRequest = {
    state: string;
    code: string;
};
export const getAccessToken = createAsyncThunk<AuthenticationData, AccessTokenRequest, { state: RootState }>(
    'authentication/getAccessToken',
    async (accessTokenRequest: AccessTokenRequest, { getState, rejectWithValue }) => {
        const state = getState();
        if (accessTokenRequest.state !== state.authentication.authenticationState) {
            return rejectWithValue({});
        }

        return getAccessTokenFromCodeService(
            state.settings.oAuthBaseUrl,
            state.settings.apiKey,
            accessTokenRequest.code,
        );
    },
);

type RefreshResult = { data: AuthenticationData; forRefreshToken: string };
type RefreshFailure = { forRefreshToken: string | undefined };

/**
 * Attempt to refresh the session using a refresh token from the store.
 *
 * Both outcomes carry `forRefreshToken` -- the exact refresh_token value this call was refreshing -- so the
 * reducers below can tell a response apart from one belonging to a session that's since been invalidated or
 * replaced by a different one. useAuthentication's refresh timer isn't torn down just because demo mode is
 * disabled (that path doesn't reload the page), so a refresh already in flight when the session changes can
 * still land after the fact; without this, its .fulfilled could resurrect an invalidated session, or its
 * .rejected could wipe out a newer, unrelated one.
 */
export const refreshAccessToken = createAsyncThunk<
    RefreshResult,
    void,
    { state: RootState; rejectValue: RefreshFailure }
>('authentication/refreshAccessToken', async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const refreshToken = state.authentication.authenticationData?.refresh_token;
    if (!refreshToken) {
        return rejectWithValue({ forRefreshToken: refreshToken });
    }
    const scope = state.authentication.authenticationData?.scope;

    try {
        const data = await refreshAccessTokenService(
            state.settings.oAuthBaseUrl,
            state.settings.apiKey,
            refreshToken,
            scope,
        );
        return { data, forRefreshToken: refreshToken };
    } catch {
        // Routed through rejectWithValue (rather than left to reject on its own) so the identity check
        // below has a forRefreshToken to compare against for a genuine network failure too, not just the
        // no-refresh-token-at-all case above.
        return rejectWithValue({ forRefreshToken: refreshToken });
    }
});

/**
 * Invalidate current authentication and clear the state.
 *
 * Clears local state synchronously, up front, rather than only once (or if) the revoke_token round trip
 * below completes: useAuthentication's refresh timer isn't torn down just because demo mode was disabled
 * (that path doesn't reload the page), so it can still be running when this fires. If clearing only happened
 * in a .fulfilled/.rejected reducer, a refresh already in flight at that moment could land its own
 * .fulfilled handler afterwards and repopulate authenticationData with the very session this was meant to
 * invalidate. Capturing refreshToken/scope before dispatching the clear (not after, and not via getState()
 * inside a .pending reducer -- either would read the state this itself just cleared) keeps the revoke call
 * working correctly despite state already being gone by the time it's awaited.
 */
export const invalidateAuthentication = createAsyncThunk<void, void, { state: RootState }>(
    'authentication/invalidateAuthentication',
    async (_: void, { getState, dispatch }) => {
        const state = getState();
        const refreshToken = state.authentication.authenticationData?.refresh_token;
        const scope = state.authentication.authenticationData?.scope;
        const { oAuthBaseUrl, apiKey } = state.settings;

        dispatch(authenticationSlice.actions.authenticationCleared());

        if (!refreshToken) {
            return;
        }
        await revokeRefreshTokenService(oAuthBaseUrl, apiKey, refreshToken, scope);
    },
);

// Thunks

/**
 * Initializes an authentication flow by redirecting the user to the authentication service.
 *
 * @param language the language to use on the authentication service
 * @param governmentId if specified used to create a login hint to be used for the authentication
 * @param overrides specify values to override (scope and redirectUrl)
 */
export const initAuthentication =
    (
        governmentId?: string,
        overrides?: { scope?: string; redirectUrl?: string },
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();

        const scopeToUse = overrides?.scope ? overrides.scope : state.settings.authenticationScope;
        const redirectUrlToUse = overrides?.redirectUrl
            ? overrides.redirectUrl
            : window.location.origin + window.location.pathname;
        const authenticationState = uuid();
        dispatch(authenticationSlice.actions.initiate({ authenticationState }));

        return getAuthorizationUrlService(
            state.settings.oAuthBaseUrl,
            state.settings.apiKey,
            state.settings.countryCode,
            redirectUrlToUse,
            authenticationState,
            scopeToUse,
            governmentId,
        ).then((authorizationUrl) => {
            // Ignore a /init response that resolves after it no longer corresponds to the current attempt --
            // same correlation check as getAccessToken.fulfilled/.rejected above, applied here to the redirect
            // instead of a reducer. Without this, a stale attempt's response landing after invalidateStore
            // cleared the session (or after a newer attempt already started) would still redirect the browser
            // into the old auth flow.
            if (getState().authentication.authenticationState !== authenticationState) {
                return;
            }
            redirectToAuthorizationUrl(authorizationUrl);
        });
    };

// Selectors
export const selectAccessToken = (state: any) => {
    const authenticationState = (state?.authentication ?? undefined) as AuthenticationState;
    return authenticationState?.authenticationData?.access_token;
};
export const selectValidAccessToken = (state: any) => {
    const authenticationState = (state?.authentication ?? undefined) as AuthenticationState;
    const accessToken = authenticationState?.authenticationData?.access_token;
    return isTokenValid(accessToken) ? accessToken : undefined;
};

/**
 * The `state` value generated by the most recent initAuthentication call in this session, persisted across the
 * full-page redirect to the auth service. Used to verify that an OAuth error/callback redirect actually
 * corresponds to an authentication attempt this session initiated, rather than trusting a bare URL param.
 */
export const selectAuthenticationState = (state: any) => {
    const authenticationState = (state?.authentication ?? undefined) as AuthenticationState;
    return authenticationState?.authenticationState;
};

export const authenticationSlice = createSlice({
    name: 'authentication',
    initialState: initialAuthenticationState,
    reducers: {
        initiate: (state: AuthenticationState, action: PayloadAction<{ authenticationState: string }>) => {
            state.authenticationState = action.payload.authenticationState;
            state.authenticationData = undefined;
        },
        // Clears authenticationState once a redirect that relied on it for correlation (e.g. a genuine SBL
        // failure via error redirect) has been consumed, so the same state value -- persisted across the
        // BankID redirect via redux-persist -- can't be replayed by revisiting the same error+state URL later.
        authenticationStateConsumed: (state: AuthenticationState) => {
            state.authenticationState = undefined;
        },
        // Internal to invalidateAuthentication's own executor (dispatched via authenticationSlice.actions,
        // not exported) -- see that thunk's doc comment for why clearing happens here instead of an
        // extraReducers case on its .pending/.fulfilled/.rejected lifecycle.
        authenticationCleared: (state: AuthenticationState) => {
            state.authenticationState = undefined;
            state.authenticationData = undefined;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAccessToken.fulfilled, (state, action) => {
            // Ignore an exchange that completes after it no longer corresponds to the current attempt --
            // the same authenticationState correlation already checked before the request was sent (there
            // to guard against CSRF/replay) can equally have changed by the time the response lands, e.g. if
            // the session was invalidated or a new attempt started while this exchange was in flight.
            // Optional-chained: a hand-built action (some tests dispatch a plain { type, payload } shape
            // directly, skipping the real action creator) has no `meta` at all, and should fall through to
            // the comparison below rather than throw -- undefined only matches an equally-unset
            // authenticationState, which is what those tests' own setup leaves it as.
            if (action.meta?.arg?.state !== state.authenticationState) return;
            state.authenticationState = undefined;
            state.authenticationData = action.payload;
        });
        builder.addCase(getAccessToken.rejected, (state, action) => {
            if (action.meta?.arg?.state !== state.authenticationState) return;
            state.authenticationState = undefined;
            state.authenticationData = undefined;
        });
        builder.addCase(refreshAccessToken.fulfilled, (state, action) => {
            // Only apply if the session being refreshed is still the one currently active -- ties the
            // response to the specific session it belongs to (via its refresh_token) rather than merely
            // checking that *some* session is active, which a stale refresh from an already-invalidated-
            // and-replaced session would still satisfy. See refreshAccessToken's own doc comment.
            // Optional-chained on both sides for the same reason as getAccessToken.fulfilled above: kept
            // consistent with .rejected below rather than relying on the payload always being present.
            if (state.authenticationData?.refresh_token !== action.payload?.forRefreshToken) return;
            state.authenticationData = action.payload.data;
        });
        builder.addCase(refreshAccessToken.rejected, (state, action) => {
            if (state.authenticationData?.refresh_token !== action.payload?.forRefreshToken) return;
            state.authenticationData = undefined;
        });
        // No .pending/.fulfilled/.rejected cases for invalidateAuthentication: it dispatches its own
        // synchronous authenticationCleared() up front instead -- see its doc comment for why.
    },
});

// Exported as named action creators (not the whole slice) to keep the public API surface narrow, matching
// the other store barrels (e.g. settings, applicationform), which only export specific actions/selectors.
export const { initiate: authenticationInitiated, authenticationStateConsumed } = authenticationSlice.actions;

export default authenticationSlice.reducer;
